// Learning Framework Builder - recommendation + ROI + tier engine (concept).
//
// All numbers are concept estimates derived from the offline Learning Framework
// model (industry benchmarks + build-time savings), parameterised by the admin's
// answers. Constants are grouped at the top so they're easy to tune.

import { FW_CATALOG, FwProgram, FwFunctionKey, READY_BUILT_IDS, teamLabel } from './frameworkData';
import { tierName } from '../../../../../../components/gamification/scorecardData';

export interface FwAnswers {
  website?: string;
  industry?: string;
  industryOther?: string;
  countries: string[];
  employees?: number | null;
  managers?: number | null;
  hiredLast?: string;
  hireNext?: string;
  functions: string[];
  activities: string[];
  priorities: string[];
  aiCapability?: AiLevel;
  delivery?: string;
  currentPrograms: string[];
  challenges?: string;
  traits: string[];
}

export const emptyAnswers = (): FwAnswers => ({
  countries: [],
  functions: [],
  activities: [],
  priorities: [],
  currentPrograms: [],
  traits: [],
  employees: null,
  managers: null
});

// ── Recommendation rules ─────────────────────────────────────────────────────
// Score candidate programs, then take the top few per pillar so the framework is
// balanced across columns and capped at ~15 (fewer is fine). AI programs are
// gated by the team's stated AI capability so only the relevant ones appear.
// Anything the org already runs (Q7) is included whatever the caps: it is part of
// their learning, so the framework shows it too. So is the development their
// answers call for (people managers, priorities, hiring plans, team count,
// headcount), so development is never AI courses alone when a signal applies. Every score carries the reason
// it was given, and a program's basis (the "why we recommend this" tooltip) is
// built from its strongest reasons, so it cites what the admin actually told us.
// Every recommendation is a course: Assessments aren't available yet.

export type AiLevel = 'none' | 'basic' | 'adopting' | 'advanced';

const MAX_TOTAL = 15;
const PILLAR_TARGET: Record<string, number> = { onboard: 4, operate: 4, perform: 4, lead: 3 };

// AI programs to surface at each capability level (everything else stays off).
const AI_BY_LEVEL: Record<AiLevel, string[]> = {
  none: ['ai-foundations', 'first-ai-tools', 'ai-in-your-workday'],
  basic: ['ai-foundations', 'ai-in-your-workday', 'prompting'],
  adopting: ['ai-in-your-workday', 'prompting', 'advanced-ai'],
  advanced: ['advanced-ai', 'prompting']
};
const FN_AI: Record<FwFunctionKey, string> = {
  sales: 'ai-in-sales',
  marketing: 'ai-in-marketing',
  operations: 'ai-in-operations',
  finance: 'ai-in-finance',
  hr: 'ai-in-hr',
  engineering: 'ai-for-developers',
  customer: 'ai-in-your-workday'
};

// ── Reason copy ──
// The admin's answers in plain English, so every reason reads as a full sentence
// about their company.
const COUNTRY_NAMES: Record<string, string> = {
  us: 'the United States',
  uk: 'the United Kingdom',
  ca: 'Canada',
  ie: 'Ireland',
  au: 'Australia',
  de: 'Germany',
  fr: 'France'
};
const listPhrase = (items: string[]): string =>
  items.length <= 1 ? items.join('') : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
const INDUSTRY_PHRASE: Record<string, string> = {
  hospitality: 'a hospitality, food and beverage business',
  retail: 'a retail business',
  technology: 'a technology and software business',
  healthcare: 'a healthcare and life sciences business',
  financial: 'a financial services business',
  professional: 'a professional services firm',
  construction: 'a construction and trades business',
  manufacturing: 'a manufacturing and logistics business'
};
const HIRE_PHRASE: Record<string, string> = {
  lt5: 'fewer than 5',
  '5-15': '5 to 15',
  '16-30': '16 to 30',
  '31-50': '31 to 50',
  '50+': 'more than 50'
};
const HIRE_16_PLUS = new Set(['16-30', '31-50', '50+']);
const AI_LEVEL_PHRASE: Record<AiLevel, string> = {
  none: 'your people are just getting started with AI',
  basic: 'some of your people already use AI informally',
  adopting: 'your teams are actively adopting AI',
  advanced: 'AI is already embedded in how your teams work'
};
// Why each AI program is the next step at the level the admin described.
const AI_NEXT_STEP: Record<string, string> = {
  'ai-foundations': 'A shared foundation gets everyone to the same confident starting point before use spreads.',
  'first-ai-tools': 'Knowing which tool to reach for is what gets people past the first few experiments.',
  'ai-in-your-workday': 'The quickest return comes from using AI on the everyday work people already do.',
  prompting: 'Better prompts are what turn occasional use into reliable, repeatable results.',
  'advanced-ai': 'The next gains come from deep research, custom assistants and agents that take on multi-step work.'
};
const PRIORITY_PHRASE: Record<string, string> = {
  onboarding: 'onboarding new hires faster',
  retention: 'reducing turnover and improving retention',
  leadership: 'building management and leadership',
  performance: 'improving team and role performance',
  compliance: 'meeting compliance and risk obligations',
  ai: 'upskilling your people on AI',
  standardise: 'standardizing how work gets done'
};
// "You told us you already run …" for each program the questionnaire offers (Q7).
// Company onboarding is the Welcome course and role-specific skills are the team
// courses, so those two aren't pinned from the catalog.
const ALREADY_RUN_PHRASE: Record<string, string> = {
  'company-onboarding': 'company onboarding',
  'role-onboarding': 'role-specific onboarding',
  'role-skills': 'role-specific skills training',
  'product-knowledge': 'product knowledge training',
  'customer-service': 'customer service training',
  'first-time-manager': 'first-time manager training',
  'management-essentials': 'management essentials training',
  'leadership-development': 'a leadership development program',
  'workplace-conduct': 'anti-harassment and workplace conduct training',
  'data-protection': 'data protection and privacy training',
  'info-security': 'information security training',
  'code-of-conduct': 'code of conduct and ethics training',
  mentorship: 'a mentorship program',
  'career-development': 'career development and performance conversations',
  'ai-foundations': 'AI and technology upskilling',
  'comms-collaboration': 'communication and collaboration training'
};
const BUILT_SEPARATELY = new Set(['company-onboarding', 'role-skills']);

const priorityReason = (key: string): string =>
  `You named ${PRIORITY_PHRASE[key]} as a top priority for the next 6 to 12 months.`;

// The data protection regimes that apply in the countries the admin picked.
function privacyRegimes(countries: Set<string>): string[] {
  const regimes: string[] = [];
  if (countries.has('uk')) regimes.push('UK GDPR');
  if (countries.has('ie') || countries.has('de') || countries.has('fr')) regimes.push('EU GDPR');
  if (countries.has('us')) regimes.push('US state privacy laws such as the CCPA');
  if (countries.has('ca')) regimes.push("Canada's PIPEDA");
  if (countries.has('au')) regimes.push("Australia's Privacy Act");
  return regimes;
}

// One Role Excellence course per team the org selected, in the OPERATE column.
// Named for what the team is trying to achieve, described by what the course
// should cover, with the team's own reason it matters. Teams typed in as "Other"
// get a general version.
const TEAM_COURSES: Record<string, { name: string; description: string; why: string }> = {
  sales: {
    name: 'Running a winning sales process',
    description:
      'Each stage of the sales cycle, from qualifying and discovery to proposals, negotiation and close. Covers running a strong discovery call, handling common objections, and keeping deals moving with clear next steps. Finishes with pipeline hygiene and forecasting, so the numbers can be trusted.',
    why: 'Consistent results depend on every rep following the same disciplined process, from first call to signed contract.'
  },
  marketing: {
    name: 'Marketing that drives pipeline',
    description:
      'How to define the ideal customer, build messaging that lands with them, and plan campaigns around measurable goals. Covers working closely with Sales on lead quality and handover, and reading campaign results to double down on what works.',
    why: 'Marketing earns its budget by creating pipeline Sales can close, which takes shared goals and clear measurement.'
  },
  customer: {
    name: 'Delivering standout customer support',
    description:
      'Handling every request consistently and with empathy: clear written replies, calming frustrated customers, and knowing when and how to escalate. Covers working to service levels, using the support tools well, and turning recurring issues into feedback the product team can act on.',
    why: 'Support is where many customers decide whether to stay, so every reply needs to meet the same standard.'
  },
  'customer-success': {
    name: 'Keeping customers successful and growing',
    description:
      'Getting new customers to value quickly, running check-ins and business reviews customers find useful, and spotting the early signs of churn. Covers building relationships beyond a single contact and recognizing the right moment to talk about expansion.',
    why: 'Keeping and growing existing customers costs far less than winning new ones, and it depends on the whole team working the same way.'
  },
  product: {
    name: 'Building products customers value',
    description:
      'Talking to customers to find the problems worth solving, prioritizing the roadmap with clear trade-offs, and writing requirements engineering can build from. Covers measuring what happens after launch and sharing product decisions clearly with the rest of the business.',
    why: 'Building the right things depends on a repeatable way of learning from customers and making trade-offs.'
  },
  engineering: {
    name: 'Shipping quality software with confidence',
    description:
      'The engineering practices that keep quality high while the team moves fast: code review standards, testing, secure coding basics and clear documentation. Covers handling incidents calmly, learning from them, and keeping technical debt under control.',
    why: 'As the codebase and the team grow, shared engineering standards stop quality and speed pulling against each other.'
  },
  operations: {
    name: 'Running smooth, scalable operations',
    description:
      'Mapping and documenting the core processes, writing standard procedures people actually follow, and measuring how well work flows. Covers spotting bottlenecks, making small improvements that stick, and keeping operations steady as volume grows.',
    why: 'Documented, measured processes are what let operations scale without headcount growing at the same rate.'
  },
  finance: {
    name: 'Partnering with the business on finance',
    description:
      'Closing the month accurately and on time, building budgets and forecasts the business can plan around, and keeping controls tight. Covers explaining the numbers clearly to non-finance colleagues so they can make better decisions.',
    why: 'The rest of the business relies on Finance for accurate numbers and clear guidance to plan around.'
  },
  hr: {
    name: 'Running a people function that scales',
    description:
      'Hiring well, onboarding consistently, and running performance and development cycles that managers value. Covers handling employee relations issues fairly, keeping people data accurate, and keeping policies up to date.',
    why: 'How consistently people are hired, onboarded and developed shapes every other team in the business.'
  },
  legal: {
    name: 'Giving practical legal guidance',
    description:
      'Prioritizing contract reviews by risk, giving clear advice the business can act on quickly, and working closely with commercial teams. Covers keeping templates and playbooks current and getting the best from outside counsel.',
    why: 'Legal advice is most valuable when it is fast, practical and focused on the risks that matter most.'
  },
  it: {
    name: 'Keeping systems reliable and secure',
    description:
      'Managing access, devices and software across the company, running a responsive service desk, and handling incidents quickly. Covers keeping an accurate record of systems and licenses, and making joiners and leavers smooth.',
    why: 'Every team depends on systems that work and access that is managed securely.'
  },
  data: {
    name: 'Turning data into decisions',
    description:
      'Keeping data accurate and well defined, building reports and dashboards people actually use, and answering business questions with clear analysis. Covers presenting findings so they lead to decisions, and handling personal data responsibly.',
    why: 'Data only creates value when people trust it and it leads to better decisions.'
  },
  design: {
    name: 'Designing with real customer insight',
    description:
      'Grounding design decisions in user research, running useful critiques, and keeping a consistent design system. Covers working closely with product and engineering, from early concepts through to a smooth handoff.',
    why: 'Design grounded in research cuts rework and leads to products customers find easier to use.'
  }
};

function buildTeamCourses(answers: FwAnswers): FwProgram[] {
  const seen = new Set<string>();
  return (answers.functions || [])
    .map((t) => (t || '').trim())
    .filter((t) => {
      const key = t.toLowerCase();
      if (!t || seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .map((team): FwProgram => {
      const label = teamLabel(team);
      const course = TEAM_COURSES[team] ?? {
        name: `Excelling in ${label}`,
        description: `The core skills, tools and working practices the ${label} team relies on every day. Covers what good looks like in the role, the situations the team handles most often, and the habits that help it deliver consistently.`,
        why: `A course built around the team's day-to-day work gives everyone in it the same clear standard of what good looks like.`
      };
      return {
        id: `team-course-${team.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
        name: course.name,
        description: course.description,
        pillar: 'operate',
        type: 'role-excellence',
        format: 'course',
        templateReady: false,
        tags: ['team'],
        basis: `You told us ${label} is one of your main teams. ${course.why}`
      };
    });
}

// A company onboarding course every organisation should run, named for the org.
// The one program that carries the company name.
function buildOnboarding(answers: FwAnswers, orgName?: string): FwProgram {
  const company = (orgName || '').trim();
  const hires = answers.hireNext ? HIRE_PHRASE[answers.hireNext] : '';
  const why = [
    answers.currentPrograms.includes('company-onboarding')
      ? 'You told us you already run company onboarding, so it leads your Program Plan as the course every new hire starts with.'
      : hires
      ? `You plan to hire ${hires} people over the next 12 months, and every one of them needs the same strong start, whichever team they join.`
      : 'Every new hire needs the same strong start, whichever team they join.',
    answers.priorities.includes('onboarding')
      ? priorityReason('onboarding')
      : 'A structured welcome helps people become productive sooner and makes them more likely to stay.'
  ];
  return {
    id: 'onboarding-welcome',
    name: company ? `Welcome to ${company}` : 'Welcome to the team',
    description: `Everything a new hire needs in their first weeks: the mission and values, how the company works, who's who, and the tools and systems they will use day to day. Finishes with what great looks like in their role and the milestones for their first 90 days.`,
    pillar: 'onboard',
    type: 'role-excellence',
    format: 'course',
    templateReady: false,
    tags: ['onboarding'],
    basis: why.join(' ')
  };
}

// Make Standards & Compliance reference the org's actual jurisdictions, so they
// read as specific and objectively accurate rather than generic. (A deterministic
// approximation - the full per-jurisdiction accuracy is Elliott's job.)
function annotateStandards(p: FwProgram, answers: FwAnswers): FwProgram {
  if (p.type !== 'standards') return p;
  if (p.id === 'data-protection') {
    const regimes = privacyRegimes(new Set(answers.countries));
    if (regimes.length) {
      return { ...p, description: `${p.description} Covers what ${listPhrase(regimes)} require in day-to-day work.` };
    }
  }
  return p;
}

export function recommend(answers: FwAnswers, orgName?: string): FwProgram[] {
  const scores = new Map<string, number>();
  const reasons = new Map<string, { score: number; text: string }[]>();
  // A program keeps its highest score and every distinct reason it was given.
  const bump = (id: string, s: number, reason: string) => {
    scores.set(id, Math.max(scores.get(id) ?? 0, s));
    const list = reasons.get(id) ?? [];
    if (!list.some((r) => r.text === reason)) list.push({ score: s, text: reason });
    reasons.set(id, list);
  };
  // Development a signal in the answers calls for is pinned into the framework
  // like the programs they already run (see `chosen`), so the per-pillar caps
  // never trade it away for an AI course.
  const signalled = new Set<string>();
  const signal = (id: string, s: number, reason: string) => {
    bump(id, s, reason);
    signalled.add(id);
  };

  const employees = answers.employees ?? 75;
  const managers = answers.managers ?? 0;
  const fns = new Set(answers.functions);
  const traits = new Set(answers.traits);
  const countries = new Set(answers.countries);
  const ai: AiLevel = (answers.aiCapability as AiLevel) || 'basic';
  // Reason phrases only ever repeat what the admin actually answered.
  const where = listPhrase(answers.countries.map((c) => COUNTRY_NAMES[c] || c));
  const industry = answers.industry ? INDUSTRY_PHRASE[answers.industry] : '';
  const headcount = answers.employees ? `${answers.employees.toLocaleString('en-US')} people` : '';
  const managerPhrase = `${managers} people manager${managers === 1 ? '' : 's'}`;
  const aiToldUs = answers.aiCapability ? `You told us ${AI_LEVEL_PHRASE[ai]}.` : '';

  // Programs they already run are pinned into the framework (see `chosen`).
  const alreadyRun = new Set(
    answers.currentPrograms.filter((id) => !BUILT_SEPARATELY.has(id) && FW_CATALOG.some((p) => p.id === id))
  );
  alreadyRun.forEach((id) =>
    bump(
      id,
      100,
      `You told us you already run ${ALREADY_RUN_PHRASE[id]}. Including it keeps that program in your Program Plan alongside the new recommendations, so your people can see everything on offer in one place.`
    )
  );

  // Core, everyone.
  bump(
    'info-security',
    92,
    headcount
      ? `With ${headcount} using company systems and data, a single phishing email or weak password can expose the whole business.`
      : 'A single phishing email or weak password can expose the whole business, and most breaches start with a human mistake.'
  );
  bump(
    'workplace-conduct',
    90,
    where
      ? `As an employer in ${where}, you're responsible for preventing harassment and discrimination at work.`
      : "As an employer, you're responsible for preventing harassment and discrimination at work."
  );
  const regimes = privacyRegimes(countries);
  bump(
    'data-protection',
    88,
    regimes.length
      ? `As a company operating in ${where}, you're subject to ${listPhrase(regimes)}, which set the rules for how your people collect, use and share personal data.`
      : 'Anyone who handles customer or employee information needs to know how to keep it safe and use it lawfully.'
  );
  bump(
    'responsible-ai-use',
    86,
    aiToldUs
      ? `${aiToldUs} Everyone needs the same clear rules on what is safe to share with AI tools and when a person must check the output.`
      : 'Clear rules on what is safe to share with AI tools, and when a person must check the output, keep confidential and customer data protected as AI use grows.'
  );

  // AI, gated by capability.
  AI_BY_LEVEL[ai].forEach((id, i) =>
    bump(id, 76 - i * 2, aiToldUs ? `${aiToldUs} ${AI_NEXT_STEP[id]}` : AI_NEXT_STEP[id])
  );
  if (ai === 'adopting' || ai === 'advanced') {
    (Array.from(fns) as FwFunctionKey[]).forEach((f) => {
      if (FN_AI[f]) {
        bump(
          FN_AI[f],
          66,
          `You told us you have a ${teamLabel(f)} team and ${AI_LEVEL_PHRASE[ai]}. Showing that team how AI fits its own work is where adoption turns into real time saved.`
        );
      }
    });
    if (managers > 0) {
      bump(
        'ai-for-managers',
        64,
        `You told us you have ${managerPhrase} and ${AI_LEVEL_PHRASE[ai]}. Managers set the example for how their teams use AI, so they need to be confident with it first.`
      );
    }
  }

  // Management / leadership.
  if (managers > 0 || employees >= 50) {
    const managersToldUs =
      managers > 0
        ? `You told us you have ${managerPhrase}${headcount ? ` across ${headcount}` : ''}.`
        : headcount
        ? `At ${headcount}, you need people who can lead teams well, whether they manage now or are about to.`
        : '';
    const develop = managers > 0 ? signal : bump;
    develop(
      'first-time-manager',
      73,
      `${managersToldUs} Most managers are promoted for being good at their own work, so new managers need support to lead others well.`.trim()
    );
    develop(
      'management-essentials',
      71,
      `${managersToldUs} Shared management fundamentals mean people get the same quality of leadership whichever team they are in.`.trim()
    );
  }
  if (employees >= 250) {
    signal(
      'leadership-development',
      75,
      `At ${headcount}, you need leaders who can set direction beyond their own team and take the business through its next stage of growth.`
    );
  }
  // Hiring 16 or more people in the next 12 months: mentoring, so new hires ramp
  // up with support.
  if (HIRE_16_PLUS.has(answers.hireNext || '')) {
    signal(
      'mentorship',
      70,
      `You plan to hire ${HIRE_PHRASE[answers.hireNext as string]} people over the next 12 months, and pairing each of them with a mentor is one of the fastest ways to get new hires productive and keep them.`
    );
  }
  // Three or more teams: communicating and working across teams.
  if (fns.size >= 3) {
    signal(
      'comms-collaboration',
      68,
      `You told us you have ${fns.size} teams, and work gets lost or duplicated when teams don't communicate clearly with each other.`
    );
  }
  if (traits.has('promote')) bump('first-time-manager', 73, 'You told us you plan to promote people into their first management roles.');

  // Function-specific (non-AI).
  if (fns.has('customer')) {
    bump('customer-service', 62, 'You told us you have a Customer Support team, and every conversation they have shapes how customers see the business.');
  }
  if (fns.has('finance')) {
    bump('financial-controls', 58, 'You told us you have a Finance team, and consistent financial controls protect the business from costly errors and fraud.');
  }

  // Country compliance.
  if (countries.has('uk')) {
    bump(
      'anti-bribery-uk',
      56,
      "As a company operating in the United Kingdom, you're covered by the UK Bribery Act 2010, which can hold the company liable when anyone acting for it pays a bribe and it has no adequate procedures to prevent it."
    );
  }
  if (countries.has('us')) {
    bump(
      'anti-bribery-us',
      56,
      "As a company operating in the United States, you're covered by the Foreign Corrupt Practices Act, which makes it a crime to bribe foreign officials to win or keep business."
    );
  }

  // Industry - nudge the programs that matter most for each sector.
  if (answers.industry) {
    const ind = answers.industry;
    bump('industry-fundamentals', 50, `You told us you are ${industry || `in ${ind}`}.`);
    if (ind === 'financial') {
      bump('financial-controls', 64, 'As a financial services business, weak financial controls carry regulatory as well as commercial risk.');
      bump('conflicts-of-interest', 54, "As a financial services business, you're expected to identify and manage conflicts of interest so customers are treated fairly.");
    }
    if (ind === 'healthcare') {
      bump('data-protection', 93, 'As a healthcare and life sciences business, your people handle some of the most sensitive personal data there is.');
      bump('confidentiality', 63, 'As a healthcare and life sciences business, patient and research information has to stay confidential.');
    }
    if (ind === 'hospitality' || ind === 'retail') {
      bump('customer-service', 66, `As ${industry}, every customer interaction shapes your reputation and whether people come back.`);
      bump('workplace-conduct', 91, `As ${industry}, your teams deal with the public every day, so clear standards of conduct protect staff and customers alike.`);
    }
    if (ind === 'technology') {
      bump('ip-protection', 58, 'As a technology and software business, your code, product designs and roadmap are among your most valuable assets.');
    }
    if (ind === 'construction' || ind === 'manufacturing') {
      bump('risk-awareness', 60, `As ${industry}, your people work around real physical and operational risk, so problems need to be raised early.`);
    }
    if (ind === 'professional') {
      bump('confidentiality', 62, 'As a professional services firm, keeping client information confidential is central to the trust your business depends on.');
    }
  }

  // Traits.
  if (traits.has('remote') || traits.has('multisite')) {
    bump('comms-collaboration', 67, 'You told us your people work remotely or across sites, where clear communication stops work being lost between teams.');
  }
  if (traits.has('turnover')) {
    bump('feedback-performance', 69, 'You told us turnover is a challenge, and poor feedback is one of the most common reasons people leave.');
    bump('career-development', 65, 'You told us turnover is a challenge, and people are far more likely to stay when they can see how to grow.');
  }
  if (traits.has('incident')) {
    const incidentWhy = 'You told us you have had incidents to learn from, and most start with information or risks that were not handled well.';
    bump('confidentiality', 61, incidentWhy);
    bump('risk-awareness', 57, incidentWhy);
    bump('records-management', 53, incidentWhy);
  }
  if (traits.has('intern')) {
    bump('early-careers', 72, 'You told us you hire graduates or interns, who need structure and support to contribute quickly.');
    bump('mentorship', 60, 'You told us you hire graduates or interns, and mentoring is one of the fastest ways to help them grow.');
  }
  if (traits.has('ma')) {
    const maWhy = 'You told us you are going through a merger or acquisition, when shared standards and steady operations matter most.';
    bump('business-continuity', 51, maWhy);
    bump('code-of-conduct', 59, maWhy);
  }

  // Baseline fill so PERFORM / development is never empty.
  bump(
    'feedback-performance',
    46,
    managers > 0
      ? `You told us you have ${managerPhrase}, and the quality of their feedback is one of the strongest drivers of performance and engagement in their teams.`
      : 'Regular, useful feedback is one of the strongest drivers of performance and engagement in any team.'
  );
  bump(
    'comms-collaboration',
    45,
    fns.size >= 2
      ? `You told us you have ${fns.size} teams, and work gets lost or duplicated when teams don't communicate clearly with each other.`
      : 'Clear communication between people and teams stops work being lost or duplicated.'
  );
  bump(
    'career-development',
    44,
    `People are far more likely to stay when they can see a clear path to grow${headcount ? `, and at ${headcount} every avoidable leaver is costly to replace` : ''}.`
  );

  // Compliance driven by what the business actually does - accuracy matters most
  // here, so these map directly from the activities the admin confirmed.
  const activities = new Set(answers.activities);
  if (activities.has('personal-data')) {
    bump('data-protection', 94, 'You told us you handle customer or employee personal data, so a mistake can quickly become a reportable breach.');
    bump('info-security', 93, 'You told us you handle customer or employee personal data, which raises the cost of any security breach.');
  }
  if (activities.has('health-data')) {
    bump('data-protection', 95, 'You told us you handle health or medical information, which data protection law treats as especially sensitive.');
    bump('confidentiality', 70, 'You told us you handle health or medical information, which must only ever be shared with people who need it.');
  }
  if (activities.has('payments')) {
    bump('financial-controls', 72, 'You told us you take payments or handle financial transactions, so everyone involved needs to follow the same controls to prevent errors and fraud.');
  }
  if (activities.has('financial-advice')) {
    bump('financial-controls', 74, 'You told us you provide financial products or advice, where weak controls carry regulatory as well as commercial risk.');
    bump('conflicts-of-interest', 66, 'You told us you provide financial products or advice, where an undisclosed conflict of interest can harm customers and breach regulatory expectations.');
  }
  if (activities.has('safety-critical')) {
    bump('risk-awareness', 70, 'You told us your people do physically demanding or safety-critical work, where raising risks early prevents injuries.');
  }
  if (activities.has('government')) {
    const publicSectorWhy = 'You told us you sell to government or the public sector, where the risk of being offered or asked for an improper payment is higher.';
    bump('business-ethics', 64, 'You told us you sell to government or the public sector, where ethical standards are held to a higher bar.');
    if (countries.has('uk')) bump('anti-bribery-uk', 72, publicSectorWhy);
    if (countries.has('us')) bump('anti-bribery-us', 72, publicSectorWhy);
  }
  if (activities.has('listed')) {
    bump('conflicts-of-interest', 64, 'As a publicly listed company, you need to show investors and regulators that conflicts of interest are disclosed and managed.');
    bump(
      'business-ethics',
      62,
      'As a publicly listed company, your conduct is closely scrutinized by investors and regulators.'
    );
  }

  // Priorities lead the mix toward what the L&D team said matters most.
  const priorities = new Set(answers.priorities);
  if (priorities.has('retention')) {
    signal('feedback-performance', 72, priorityReason('retention'));
    signal('career-development', 68, priorityReason('retention'));
  }
  if (priorities.has('leadership')) {
    signal('first-time-manager', 76, priorityReason('leadership'));
    signal('management-essentials', 72, priorityReason('leadership'));
  }
  if (priorities.has('ai')) AI_BY_LEVEL[ai].forEach((id, i) => bump(id, 80 - i * 2, priorityReason('ai')));
  if (priorities.has('performance')) signal('feedback-performance', 66, priorityReason('performance'));
  if (priorities.has('compliance')) {
    bump('info-security', 94, priorityReason('compliance'));
    bump('workplace-conduct', 92, priorityReason('compliance'));
    bump('data-protection', 90, priorityReason('compliance'));
  }

  // Resolve to catalog, honouring per-program function / country gating.
  // Role Excellence is the per-team courses (built below), so generic
  // role-excellence catalog items are excluded here unless they already run one.
  // What remains is Development + Standards & Compliance.
  const candidates = FW_CATALOG.filter((p) => {
    if (p.type === 'role-excellence' && !alreadyRun.has(p.id)) return false;
    if (!scores.has(p.id)) return false;
    if (p.functions && p.functions.length && !p.functions.some((f) => fns.has(f))) return false;
    if (p.country && !countries.has(p.country)) return false;
    return true;
  }).map((p) => ({ p, score: scores.get(p.id) as number }));

  // Top few per pillar (balances the columns), then cap the total.
  const byPillar: Record<string, { p: FwProgram; score: number }[]> = {};
  candidates.forEach((c) => {
    if (!byPillar[c.p.pillar]) byPillar[c.p.pillar] = [];
    byPillar[c.p.pillar].push(c);
  });
  let picked: { p: FwProgram; score: number }[] = [];
  Object.keys(byPillar).forEach((pillar) => {
    byPillar[pillar].sort((a, b) => b.score - a.score);
    picked = picked.concat(byPillar[pillar].slice(0, PILLAR_TARGET[pillar] ?? 4));
  });
  if (picked.length > MAX_TOTAL) {
    picked.sort((a, b) => b.score - a.score);
    picked = picked.slice(0, MAX_TOTAL);
  }

  // The basis is the program's two strongest reasons, kept in the order they
  // were given (the rules run from the broad baseline to the specific).
  const basisFor = (id: string): string => {
    const list = reasons.get(id) ?? [];
    const strongest = [...list].sort((a, b) => b.score - a.score).slice(0, 2);
    return list
      .filter((r) => strongest.includes(r))
      .map((r) => r.text)
      .join(' ');
  };
  // Already-run and signal-driven programs are in regardless of the per-pillar
  // and total caps.
  const chosen = new Set([...picked.map((c) => c.p.id), ...Array.from(alreadyRun), ...Array.from(signalled)]);
  const catalogRecs = FW_CATALOG.filter((p) => chosen.has(p.id)).map((p) => ({
    ...annotateStandards(p, answers),
    basis: basisFor(p.id)
  }));

  // Onboarding (every org) leads, then the per-team Role Excellence courses,
  // then Development + Standards.
  let finalRecs = [buildOnboarding(answers, orgName), ...buildTeamCourses(answers), ...catalogRecs];

  // Always include at least one ready-built program. If the scored set happens to
  // have none, add "Using AI safely at work" so the framework always ships with one.
  if (!finalRecs.some((p) => READY_BUILT_IDS.has(p.id))) {
    const saferAi = FW_CATALOG.find((p) => p.id === 'responsible-ai-use');
    if (saferAi) finalRecs = [...finalRecs, { ...saferAi, basis: basisFor(saferAi.id) }];
  }

  // Same shape as the live (BE) output: every program a course, readyBuilt
  // decided here.
  return finalRecs.map((p) => ({ ...p, format: 'course' as const, readyBuilt: READY_BUILT_IDS.has(p.id) }));
}

// ── ROI model (approved data points) ─────────────────────────────────────────
// Hourly rates (approved): L&D $50, manager $60, employee $31, annualised at
// 2,080 hrs/yr. Uplifts: productivity +17% (Gallup), retention +34% / fewer
// leavers (LinkedIn), onboarding ramp -50% (SHRM); replacement cost ~33% of
// salary; baseline turnover ~18%. Concept estimates - verify citations before
// customer use. Everything is anchored to the Gold Learning Tier.
const HOURS_PER_YEAR = 2080;
const LD_RATE = 50;
const MGR_RATE = 60;
const EMP_RATE = 31;
const MGR_SALARY = MGR_RATE * HOURS_PER_YEAR; // 124,800
const EMP_SALARY = EMP_RATE * HOURS_PER_YEAR; // 64,480

const LD_HOURS_PER_PROGRAM = 134; // hrs to build + maintain one program on Dosen vs manually
const RAMP_HOURS_SAVED_PER_HIRE = 120; // recovered productive time per new hire (50% faster ramp)
const KNOWLEDGE_HOURS_PER_EMPLOYEE = 26; // hrs/employee/yr saved finding knowledge

const PRODUCTIVITY_UPLIFT = 0.17; // Gallup
const RETENTION_UPLIFT = 0.34; // LinkedIn - fewer leavers
const BASELINE_TURNOVER = 0.18; // market-average annual turnover to improve on
const REPLACEMENT_COST_PCT = 0.33; // cost to replace an employee, as a share of salary

const DOSEN_COST_PER_SEAT = 320; // annual, for the ROI %

// Hire-range answers -> midpoint headcount, for the onboarding-ramp saving.
const HIRE_MIDPOINT: Record<string, number> = { lt5: 3, '5-15': 10, '16-30': 23, '31-50': 40, '50+': 65 };
function estimateNewHires(answers: FwAnswers, employees: number): number {
  const fromNext = answers.hireNext ? HIRE_MIDPOINT[answers.hireNext] : undefined;
  const fromLast = answers.hiredLast ? HIRE_MIDPOINT[answers.hiredLast] : undefined;
  return fromNext ?? fromLast ?? Math.round(employees * 0.15); // fallback ~15% of headcount
}

export interface RoiBreakdown {
  label: string;
  color: string;
  value: number;
  hours: number | null;
  detail: string; // how the value was calculated (shown in an info tooltip)
}
export interface RoiResult {
  potentialValue: number;
  totalHours: number;
  roiPct: number;
  breakdowns: RoiBreakdown[];
}

export function computeRoi(answers: FwAnswers, programCount: number): RoiResult {
  const money = (x: number) => '$' + Math.round(x).toLocaleString('en-US');
  const num = (x: number) => Math.round(x).toLocaleString('en-US');

  const employees = answers.employees ?? 75;
  const managers = Math.min(Math.max(answers.managers ?? 0, 0), employees);
  const nonManagers = Math.max(employees - managers, 0);
  const n = Math.max(programCount, 1);
  const newHires = estimateNewHires(answers, employees);

  // HR / L&D capacity - build + maintain programs on Dosen vs manually.
  const hrHours = Math.round(n * LD_HOURS_PER_PROGRAM);
  const hrValue = Math.round(hrHours * LD_RATE);

  // Employee capacity - faster onboarding (new hires) + less knowledge-search time (all staff).
  const rampHours = Math.round(newHires * RAMP_HOURS_SAVED_PER_HIRE);
  const knowledgeHours = Math.round(employees * KNOWLEDGE_HOURS_PER_EMPLOYEE);
  const empHours = rampHours + knowledgeHours;
  const empValue = Math.round(empHours * EMP_RATE);

  // Productivity & Retention.
  const payroll = managers * MGR_SALARY + nonManagers * EMP_SALARY;
  const avgSalary = employees > 0 ? payroll / employees : EMP_SALARY;
  const productivityValue = Math.round(payroll * PRODUCTIVITY_UPLIFT);
  const leaversAvoided = employees * BASELINE_TURNOVER * RETENTION_UPLIFT;
  const retentionValue = Math.round(leaversAvoided * REPLACEMENT_COST_PCT * avgSalary);
  const prodValue = productivityValue + retentionValue;

  const potentialValue = hrValue + empValue + prodValue;
  const totalHours = hrHours + empHours;
  const cost = Math.max(employees * DOSEN_COST_PER_SEAT, 1);
  const roiPct = Math.round((potentialValue / cost) * 100);

  return {
    potentialValue,
    totalHours,
    roiPct,
    breakdowns: [
      {
        label: 'HR capacity saving',
        color: '#A012FF',
        value: hrValue,
        hours: hrHours,
        detail: `${n} program${n === 1 ? '' : 's'} x ${LD_HOURS_PER_PROGRAM} hrs to build and maintain each on Dosen (vs building manually), at the L&D rate of $${LD_RATE}/hr.  ${num(hrHours)} hrs x $${LD_RATE} = ${money(hrValue)}.`
      },
      {
        label: 'Employee capacity saving',
        color: '#F79420',
        value: empValue,
        hours: empHours,
        detail: `Faster onboarding (~${newHires} new hires x ${RAMP_HOURS_SAVED_PER_HIRE} hrs saved from a 50% quicker ramp) plus less time finding knowledge (${num(employees)} staff x ${KNOWLEDGE_HOURS_PER_EMPLOYEE} hrs/yr).  ${num(empHours)} hrs x the employee rate of $${EMP_RATE}/hr = ${money(empValue)}.`
      },
      {
        label: 'Productivity & Retention',
        color: '#A6D93B',
        value: prodValue,
        hours: null,
        detail: `Productivity: a 17% uplift on payroll (${managers} manager${managers === 1 ? '' : 's'} x ${money(MGR_SALARY)} + ${num(nonManagers)} staff x ${money(EMP_SALARY)}) = ${money(productivityValue)}.  Retention: ${num(employees)} staff x 18% baseline turnover x 34% fewer leavers x 33% of salary to replace = ${money(retentionValue)}.`
      }
    ]
  };
}

// ── Organisation tier (L&D maturity) ─────────────────────────────────────────

export type FwTierKey = 'rust' | 'bronze' | 'silver' | 'gold' | 'platinum';
// The tiers carry the gamification names (Rust to Platinum, or the Activity
// levels with the Capability release), read when they are shown.
const fwTier = (key: FwTierKey, color: string): { key: FwTierKey; label: string; color: string } => ({
  key,
  color,
  get label() {
    return tierName(key === 'rust' ? 'wood' : key);
  }
});
export const FW_TIERS: { key: FwTierKey; label: string; color: string }[] = [
  fwTier('rust', '#8F6136'),
  fwTier('bronze', '#DE7F35'),
  fwTier('silver', '#C3CDD9'),
  fwTier('gold', '#DFA50D'),
  fwTier('platinum', '#3E9BE0')
];
export const tierByKey = (k: FwTierKey) => FW_TIERS.find((t) => t.key === k) ?? FW_TIERS[0];

// Benefits of reaching the Gold Learning Tier (Bersin HILO, Gold column) - shown
// under the projected tier on the results page so the tier means something.
export const GOLD_TIER_BENEFITS: string[] = [
  'Up to 24% higher team productivity',
  'New hires ramp up to 50% faster',
  'Annual churn down to 12-17% (from 35%+)',
  'Performance in the top 20% of comparable organizations'
];

export interface TierResult {
  current: FwTierKey;
  projected: FwTierKey;
}

// "Today" tier estimated from how learning is delivered (the strongest single
// maturity proxy) nudged by the breadth of programs the org already runs (Q7).
// Still an estimate, not a precise score - a real tier should come from live
// Dosen engagement. Projected is where adopting this framework would take them.
export function computeTier(answers: FwAnswers): TierResult {
  const deliveryScore: Record<string, number> = { none: 0, adhoc: 1, some: 2, most: 3, embedded: 4 };
  const d = deliveryScore[answers.delivery ?? ''] ?? 0;
  const breadth = Math.min(answers.currentPrograms.length, 6) / 6; // 0..1
  const score = d * 1.4 + breadth * 2; // 0..7.6
  const current: FwTierKey =
    score < 1.5 ? 'rust' : score < 3 ? 'bronze' : score < 4.5 ? 'silver' : score < 6 ? 'gold' : 'platinum';
  const bump: Record<FwTierKey, FwTierKey> = {
    rust: 'silver',
    bronze: 'gold',
    silver: 'gold',
    gold: 'platinum',
    platinum: 'platinum'
  };
  return { current, projected: bump[current] };
}

// Softer, non-"built for X" results subtitle.
export function companyBlurb(_answers: FwAnswers): string {
  return "These are the programs we recommend to lift your learners' effectiveness and reduce your company's risk. Add the whole Program Plan, or just the ones that matter most right now.";
}

export const currency = (n: number) => '$' + Math.round(n).toLocaleString('en-US');
